// AIDEV-NOTE: Do NOT add additional API fetches to this component.
// All data should come from the parent via props (loaded in get-session.ts).
// If you need new data, add it to get-session.ts instead.

import { useEffect, useRef, useState } from "react";

import { Link, useSearchParams } from "@remix-run/react";
import clsx from "clsx";
import { Check, FolderTree, Pencil, Quote, Shield, X } from "lucide-react";
import { toast } from "sonner";

import { EditableContent } from "~/components/editable-content";
import { PartMeditationButton } from "~/features/meditations/components/part-meditation-button";
import { FeedbackSection } from "~/features/practitioner-feedback/components/feedback";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import { LI, P, UL, H4, H5 } from "~/components/ui/typography";
import type { TPartInteractionWithImage } from "~/database/types";
import { PartImage } from "~/features/parts/components/part-detail/part-image";
import { PartRelationshipsDisplay } from "~/features/parts/components/part-relationships-display";
import { PartTypeSelector } from "~/features/parts/components/part-type-selector";
import type { ProcessedRelationship } from "~/features/parts/utils/process-relationships";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { useInternalFeatures } from "~/hooks/use-internal-features";
import { useIsMobile } from "~/hooks/use-mobile";
import { usePartMutation } from "~/hooks/use-session";
import { logger } from "~/lib/logger";
import {
    parseRelationshipArrayAttribute,
    parseStringArrayAttribute,
    type RelationshipAttribute,
} from "~/lib/part-attributes";

import { ATTRIBUTE_LIST } from "../../utils/part-types";

interface Attribute {
    name: string;
    content: string;
}

const AttributeDivider = () => (
    <div className="mx-auto my-6 h-px w-2/3 bg-brand-terra-100" />
);

// AIDEV-NOTE: v2 pipeline stores `emotions` (string[]) and `relationships`
// ({type,description,sourcePartId?,targetPartId?}[]) as JSON strings inside the
// attributes jsonb. Render + edit them as structured chips/cards. Legacy v1
// plain-text content doesn't start with "[" so it falls back to the text editor.
// An empty emotions/relationships array has no content to show, so present it
// as "Unclear" — matching every other empty attribute (dimmed + hideable).
// AIDEV-NOTE: Must narrow with the SAME parser the editor below uses. Checking the raw parse
// would call `[{"foo":"bar"}]` non-empty while the editor filters it to zero entries, rendering
// a blank row instead of the "Unclear" empty state.
const normalizeEmptyStructuredAttribute = (
    attributeKey: string,
    attribute: Attribute | undefined,
): Attribute | undefined => {
    if (!attribute) return attribute;
    const parsed =
        attributeKey === "emotions"
            ? parseStringArrayAttribute(attribute.content)
            : attributeKey === "relationships"
              ? parseRelationshipArrayAttribute(attribute.content)
              : null;
    if (parsed && parsed.length === 0) {
        return { ...attribute, content: "Unclear" };
    }
    return attribute;
};

// Emotion tags. Read-only chips by default; a pencil (revealed on hover)
// toggles edit mode with remove buttons + an add input. Each add/remove
// persists the full array as JSON.
const EmotionsField = ({
    emotions: incoming,
    onSave,
    readOnly,
}: {
    emotions: string[];
    onSave: (content: string) => Promise<void>;
    readOnly: boolean;
}) => {
    const [emotions, setEmotions] = useState(incoming);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const lastSavedRef = useRef(JSON.stringify(incoming));

    // AIDEV-NOTE: Re-seed from props only when the server value changes from
    // outside this editor (e.g. parent refetch), never mid-edit.
    const incomingKey = JSON.stringify(incoming);
    useEffect(() => {
        if (incomingKey !== lastSavedRef.current) {
            lastSavedRef.current = incomingKey;
            setEmotions(incoming);
        }
    }, [incomingKey, incoming]);

    const persist = (next: string[]) => {
        setEmotions(next);
        lastSavedRef.current = JSON.stringify(next);
        void onSave(JSON.stringify(next));
    };

    const addEmotion = () => {
        const value = draft.trim();
        setDraft("");
        if (!value) return;
        if (emotions.some((e) => e.toLowerCase() === value.toLowerCase())) {
            return;
        }
        persist([...emotions, value]);
    };

    return (
        <div className="group flex flex-wrap items-center gap-2">
            {emotions.map((emotion, index) => (
                <span
                    key={`${emotion}-${index}`}
                    className={clsx(
                        "inline-flex items-center gap-1 rounded-full border border-brand-terra-100 bg-brand-terra-50/60 py-1 text-sm capitalize text-gray-800",
                        isEditing ? "pl-3 pr-1.5" : "px-3",
                    )}
                >
                    {emotion}
                    {isEditing && (
                        <button
                            type="button"
                            aria-label={`Remove ${emotion}`}
                            onClick={() =>
                                persist(emotions.filter((_, i) => i !== index))
                            }
                            className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-brand-terra-100 hover:text-gray-700"
                        >
                            <X className="size-3.5" />
                        </button>
                    )}
                </span>
            ))}
            {isEditing && (
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            addEmotion();
                        }
                    }}
                    onBlur={addEmotion}
                    placeholder="Add emotion…"
                    className="min-w-[7rem] rounded-full border border-dashed border-brand-terra-200 bg-transparent px-3 py-1 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-terra-400 focus:outline-none"
                />
            )}
            {!readOnly && (
                <button
                    type="button"
                    aria-label={
                        isEditing ? "Done editing emotions" : "Edit emotions"
                    }
                    onClick={() => {
                        if (isEditing) addEmotion();
                        setIsEditing(!isEditing);
                    }}
                    className={clsx(
                        "rounded-full p-1.5 text-gray-400 transition-all hover:bg-brand-terra-50 hover:text-gray-700",
                        isEditing
                            ? "text-brand-terra-600"
                            : "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
                    )}
                >
                    {isEditing ? (
                        <Check className="size-4" />
                    ) : (
                        <Pencil className="size-4" />
                    )}
                </button>
            )}
        </div>
    );
};

// Relationships render as plain paragraphs — the type as a leading label and
// the description as contenteditable text that persists on blur. Clearing a
// description removes that relationship from the array.
const RelationshipsField = ({
    relationships: incoming,
    onSave,
    readOnly,
}: {
    relationships: RelationshipAttribute[];
    onSave: (content: string) => Promise<void>;
    readOnly: boolean;
}) => {
    const [items, setItems] = useState(incoming);
    const lastSavedRef = useRef(JSON.stringify(incoming));

    // AIDEV-NOTE: Re-seed from props only when the server value changes from
    // outside this editor (e.g. parent refetch), never mid-edit.
    const incomingKey = JSON.stringify(incoming);
    useEffect(() => {
        if (incomingKey !== lastSavedRef.current) {
            lastSavedRef.current = incomingKey;
            setItems(incoming);
        }
    }, [incomingKey, incoming]);

    const commitDescription = (index: number, description: string) => {
        const next = items
            .map((rel, i) => (i === index ? { ...rel, description } : rel))
            .filter((rel) => rel.description?.trim());
        const serialized = JSON.stringify(next);
        if (serialized === lastSavedRef.current) return;
        setItems(next);
        lastSavedRef.current = serialized;
        void onSave(serialized);
    };

    return (
        <div className="flex flex-col gap-3">
            {items.map((rel, index) => (
                <P key={index} className="text-lg">
                    {rel.type && (
                        <span className="font-medium capitalize">
                            {rel.type}:{" "}
                        </span>
                    )}
                    <span
                        contentEditable={!readOnly}
                        suppressContentEditableWarning
                        onBlur={(e) =>
                            commitDescription(
                                index,
                                e.currentTarget.textContent?.trim() ?? "",
                            )
                        }
                        onKeyDown={(e) => {
                            // Enter commits (via blur) instead of inserting a line break
                            if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                            }
                        }}
                        className={clsx(
                            !readOnly &&
                                "-mx-1 rounded-md px-1 focus:bg-[#fffbfb] focus:outline-none focus:ring-2 focus:ring-brand-terra-100",
                        )}
                    >
                        {rel.description}
                    </span>
                </P>
            ))}
        </div>
    );
};

const removeEvidenceLabels = (text: string) => {
    if (!text) return text;
    return text
        .replace(/\(evidence level: high\)/gi, "")
        .replace(/\(evidence level: medium\)/gi, "")
        .replace(/\(evidence level: low\)/gi, "")
        .replace(/\(high evidence\)/gi, "")
        .replace(/\(medium evidence\)/gi, "")
        .replace(/\(low evidence\)/gi, "")
        .replace(/\*/g, "")
        .trim();
};

export function PartDetailContent({
    partInteraction,
    sessionId,
    isPublicSession = false,
    inlineTitle = false,
    sessionOwnerId,
    hidePartImage,
    showPartImages = true,
    meditationsEnabledForClient,
    onImageClick,
    relationships,
    partId,
    onPartUpdated,
    partImageUrl,
}: {
    partInteraction: TPartInteractionWithImage;
    sessionId: string | undefined;
    isPublicSession?: boolean;
    inlineTitle?: boolean;
    sessionOwnerId?: string;
    hidePartImage?: boolean;
    showPartImages?: boolean;
    /** The session OWNER's meditation preference — not the viewer's. */
    meditationsEnabledForClient?: boolean;
    onImageClick?: () => void;
    relationships?: ProcessedRelationship[];
    partId?: string;
    onPartUpdated?: () => void;
    // AIDEV-NOTE: Image shown in the meditation player's left pane. Part page passes the part's
    // DEFAULT image (same as resources); session context falls back to this interaction's image.
    partImageUrl?: string;
}) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [showUnattachedBurdenInfo, setShowUnattachedBurdenInfo] =
        useState(false);
    const [hideUnclear, setHideUnclear] = useState(true);
    const isMobile = useIsMobile();

    // Get part type value, checking both direct field and attributes
    const partTypeFromAttribute = partInteraction?.attributes.find(
        (a) => a.name === "type",
    )?.content;
    const partTypeText = partInteraction?.type || partTypeFromAttribute;
    // AIDEV-NOTE: Unattached burdens (confirmed or unconfirmed) should not show the meditation button
    const isUnattachedBurden = partTypeText
        ?.toLowerCase()
        .includes("unattached burden");

    // Get recap value, checking both direct field and attributes
    const recapFromAttribute = partInteraction?.attributes.find(
        (a) => a.name === "recap",
    )?.content;
    const recap = partInteraction?.recap || recapFromAttribute;
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    const { enabled: internalFeaturesEnabled } = useInternalFeatures();
    // AIDEV-NOTE: Part grouping (Area of Life / Core Wound) is a practitioner-only
    // feature, so hide the group indicator for clients viewing their own parts.
    const { data: userDetails } = useUserDetails();
    const isPractitioner = !!userDetails?.isPractitioner;

    // Get quote value, checking both direct field and attributes
    const quoteFromAttribute = partInteraction?.attributes.find(
        (a) => a.name === "quote",
    )?.content;
    const quote = partInteraction?.quote || quoteFromAttribute;

    // AIDEV-NOTE: sessionId can be undefined for part interactions without sessions
    const { mutateAsync: mutatePart } = usePartMutation(
        sessionId || "",
        sessionOwnerId || "",
        sessionToken,
    );

    const updatePartAttribute = async (name: string, content: string) => {
        setIsUpdating(true);
        try {
            await mutatePart({
                action: "update-attribute",
                partId: partInteraction.id,
                name,
                content: content,
            });

            // AIDEV-NOTE: Call onPartUpdated callback to trigger data refresh
            if (onPartUpdated) {
                onPartUpdated();
            }

            setIsUpdating(false);
            return; // EditableContent expects the promise to resolve
        } catch (error) {
            setIsUpdating(false);
            logger
                .withError(error as Error)
                .error("Error updating part attribute");
            toast.error(`Failed to update part ${name}`);
            throw error; // EditableContent expects the promise to reject on error
        }
    };

    return (
        <div
            className={clsx(
                "flex flex-col gap-3 w-full mx-auto mt-4",
                inlineTitle && "items-start",
            )}
        >
            {partInteraction.part?.name &&
                partInteraction.name !== partInteraction.part.name && (
                    <div className="mb-4 text-sm text-muted-foreground">
                        <span className="font-medium">
                            Name in this session:
                        </span>{" "}
                        {partInteraction.name}
                    </div>
                )}
            <div className="mb-4 flex justify-start space-x-2 text-center font-normal">
                <PartTypeSelector
                    partId={partInteraction.id}
                    sessionId={sessionId || ""}
                    sessionOwnerId={sessionOwnerId || ""}
                    sessionToken={sessionToken}
                    currentPartType={partTypeText}
                    onUpdate={onPartUpdated}
                    isPublicSession={isPublicSession}
                />
            </div>

            {/* AIDEV-NOTE: Display part group below type if available (groupName comes from get-session.ts) */}
            {/* AIDEV-NOTE: Practitioner-only — grouping (Area of Life / Core Wound) is hidden for clients */}
            {isPractitioner &&
                "groupName" in partInteraction &&
                typeof partInteraction.groupName === "string" &&
                partInteraction.groupName && (
                    <div className="-mt-2 mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                        <FolderTree className="size-4" />
                        <Link
                            to="/parts/groups"
                            className="hover:text-brand-terra-500 hover:underline"
                        >
                            {partInteraction.groupName}
                        </Link>
                    </div>
                )}

            {partTypeText?.includes("Unattached Burden (Unconfirmed)") && (
                <div className="-mt-4 flex items-center gap-1 text-sm text-muted-foreground">
                    <div className="flex items-start gap-1">
                        <span>
                            Our analysis identified that this may be an
                            unattached burden. They're rare and sensitive, so
                            your confirmation is needed.
                            <Dialog
                                open={showUnattachedBurdenInfo}
                                onOpenChange={setShowUnattachedBurdenInfo}
                                analyticsId="unattached-burden-info"
                            >
                                <DialogTrigger asChild>
                                    <Button
                                        variant="link"
                                        className="mt-2 block p-0 md:ml-2 md:mt-0 md:inline-block"
                                    >
                                        Learn more
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>
                                            Unattached Burdens
                                        </DialogTitle>
                                        <DialogDescription>
                                            <P>
                                                Unattached burdens are negative
                                                beliefs or intense feeling
                                                states that:
                                            </P>
                                            <UL>
                                                <LI>
                                                    Have been internalized from
                                                    external sources
                                                </LI>
                                                <LI>
                                                    Exist independently of any
                                                    personal trauma history
                                                </LI>
                                                <LI>
                                                    Do not have protective
                                                    intentions
                                                </LI>
                                                <LI>
                                                    Present as fundamental
                                                    truths or persistent
                                                    emotional states
                                                </LI>
                                                <LI>
                                                    Cannot be traced to specific
                                                    life events or experiences
                                                </LI>
                                            </UL>
                                            <P className="text-muted-foreground">
                                                Unattached burdens are rare in
                                                IFS sessions.
                                            </P>
                                        </DialogDescription>
                                    </DialogHeader>
                                </DialogContent>
                            </Dialog>
                            <Button
                                variant="link"
                                className="mt-2 block p-0 md:ml-2 md:mt-0 md:inline-block"
                                onClick={() =>
                                    updatePartAttribute(
                                        "type",
                                        "Unattached Burden (Confirmed)",
                                    )
                                }
                            >
                                Confirm Unattached Burden
                            </Button>
                        </span>
                    </div>
                </div>
            )}
            <div className="mb-4">
                {!hidePartImage && showPartImages && (
                    <PartImage image={partInteraction.part_image} size="lg" />
                )}
            </div>
            {/* AIDEV-NOTE: Meditation play button - hidden for unattached burdens (confirmed or unconfirmed) */}
            {/* AIDEV-NOTE: Token-shared (public) views are NOT excluded here — they may play a
                meditation that already exists. PartMeditationButton itself resolves the cached
                id and renders nothing when there is none; generation stays server-blocked.
                See features/meditations/server/public-access.server.ts. */}
            {sessionId && !isUnattachedBurden && (
                <div className="mb-4">
                    <PartMeditationButton
                        sessionId={sessionId}
                        partInteractionId={partInteraction.id}
                        partName={
                            partInteraction.part?.name || partInteraction.name
                        }
                        partType={partInteraction.type}
                        meditationsEnabledForSubject={
                            meditationsEnabledForClient
                        }
                        partImageUrl={
                            partImageUrl ??
                            partInteraction.part_image?.signedUrl ??
                            undefined
                        }
                        isPublicSession={isPublicSession}
                    />
                </div>
            )}
            {quote && (
                <div className="relative mb-4 mt-2 text-lg italic">
                    <Quote className="mb-2 mr-2 size-6 -scale-100" />
                    {!isPublicSession ? (
                        <EditableContent
                            defaultValue={quote.replaceAll('"', "")}
                            onSubmit={(value) =>
                                updatePartAttribute("quote", value)
                            }
                            className="italic"
                            disabled={isPublicSession}
                        />
                    ) : (
                        <div className="italic text-gray-900">
                            {quote.replaceAll('"', "")}
                        </div>
                    )}
                </div>
            )}

            <div>
                <H4 className="text-left">Session Recap</H4>
                {!isPublicSession ? (
                    <EditableContent
                        defaultValue={recap || ""}
                        onSubmit={(value) =>
                            updatePartAttribute("recap", value)
                        }
                        className="mt-2 text-lg"
                    />
                ) : (
                    <div className="mt-2 text-lg text-gray-900">
                        {recap || ""}
                    </div>
                )}
            </div>

            {!isPublicSession && sessionId && (
                <Card className="p-2">
                    <FeedbackSection
                        sessionId={sessionId!}
                        partId={partInteraction.id}
                    />
                </Card>
            )}

            {/* Part Relationships Display */}
            {relationships && relationships.length > 0 && partId && (
                <div className="mt-6">
                    <PartRelationshipsDisplay
                        partId={partId}
                        relationships={relationships}
                    />
                </div>
            )}

            <div className="space-y-6">
                <div className="mb-8 flex flex-col items-start justify-start gap-4">
                    <H4 className="text-left">Part details</H4>
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="hide-unclear"
                            checked={hideUnclear}
                            onCheckedChange={(checked) =>
                                setHideUnclear(
                                    checked === "indeterminate"
                                        ? false
                                        : checked,
                                )
                            }
                        />
                        <label
                            htmlFor="hide-unclear"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            Hide unclear or empty details about this part.
                        </label>
                    </div>
                </div>
                {ATTRIBUTE_LIST.map((attribute) => {
                    const rawAttributeData = partInteraction?.attributes.find(
                        (a) =>
                            a.name === attribute.key ||
                            attribute.alternateKeys?.includes(a.name),
                    );

                    // AIDEV-NOTE: v2 emotions/relationships are JSON arrays. An
                    // empty array carries no signal, so normalize it to "Unclear"
                    // and let the standard hide/dim logic below handle it.
                    const attributeData = normalizeEmptyStructuredAttribute(
                        attribute.key,
                        rawAttributeData,
                    );

                    // Skip rendering if hideUnclear is true and content is "Unclear"
                    if (hideUnclear && attributeData?.content === "Unclear") {
                        return null;
                    }

                    return (
                        <div key={attribute.key}>
                            <EditableAttributeRow
                                attribute={attributeData}
                                label={attribute.label}
                                attributeKey={attribute.key}
                                partId={partInteraction.id}
                                onSubmit={(value) =>
                                    updatePartAttribute(attribute.key, value)
                                }
                                isPublicSession={isPublicSession}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Add a new component for editable attribute rows
const EditableAttributeRow = ({
    attribute,
    label,
    attributeKey,
    partId,
    onSubmit,
    isPublicSession = false,
}: {
    attribute?: Attribute;
    label: string;
    attributeKey: string;
    partId: string;
    onSubmit: (value: string) => Promise<void>;
    isPublicSession?: boolean;
}) => {
    if (!attribute) {
        return <></>;
    }

    // AIDEV-NOTE: v2 emotions/relationships arrive as JSON strings — render the
    // structured editors. Legacy plain-text v1 content (and empty arrays
    // normalized to "Unclear" upstream) don't parse as a JSON array, so they
    // fall through to the editable text path below.
    if (attributeKey === "emotions") {
        const parsed = parseStringArrayAttribute(attribute.content);
        if (parsed) {
            return (
                <>
                    <div className="mb-6 last:mb-0">
                        <H5 className="mb-2">{label}</H5>
                        <EmotionsField
                            emotions={parsed}
                            onSave={onSubmit}
                            readOnly={isPublicSession}
                        />
                    </div>
                    <AttributeDivider />
                </>
            );
        }
    }

    if (attributeKey === "relationships") {
        const parsed = parseRelationshipArrayAttribute(attribute.content);
        if (parsed) {
            return (
                <>
                    <div className="mb-6 last:mb-0">
                        <H5 className="mb-2">{label}</H5>
                        <RelationshipsField
                            relationships={parsed}
                            onSave={onSubmit}
                            readOnly={isPublicSession}
                        />
                    </div>
                    <AttributeDivider />
                </>
            );
        }
    }

    return (
        <>
            <div
                className={clsx(
                    "mb-6 last:mb-0",
                    attribute?.content === "Unclear" && "opacity-50",
                )}
            >
                <H5 className="mb-2">{label}</H5>
                {!isPublicSession ? (
                    <EditableContent
                        defaultValue={
                            removeEvidenceLabels(attribute?.content) ||
                            "Not specified"
                        }
                        onSubmit={onSubmit}
                        className="text-lg"
                        disabled={isPublicSession}
                    />
                ) : (
                    <div className="text-lg text-gray-900">
                        {removeEvidenceLabels(attribute?.content) ||
                            "Not specified"}
                    </div>
                )}
            </div>
            <AttributeDivider />
        </>
    );
};
